import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useImmer } from "use-immer";

import { GraphConfig, LineData } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsPageName } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { getSitewidePageName } from "@src/store/slices/sitewideDashboardFilters";
import { LineMetricData } from "@src/types/analytics";
import { mpAnalyticsViewChanged } from "@src/utils/mixpanel";
import { getAnalyticsPageType } from "@src/utils/mpUtils";

import { useGetTooltipProps } from "../../hooks/use-get-tooltip-props";
import { useGraphValueFormatter } from "../../hooks/use-graph-value-formatter";
import { InteractiveChartLegend } from "../../legends/InteractiveChartLegend";
import { METRIC_TO_DATA_TYPE } from "../../sitewideAnalytics/utils";
import { AxisTick } from "../chartComponents/AxisTick";
import { generateDistinctGraphColors } from "../utils";

interface CustomCursorProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  stroke?: string;
  fill?: string;
}

const CustomCursor = (props: CustomCursorProps) => {
  const theme = useTheme();
  const { x, y, width, height } = props;
  if (!height || !x || !y || !width) {
    return;
  }

  return (
    <line
      x1={x + width / 2}
      y1={y}
      x2={x + width / 2}
      y2={y + height}
      stroke={theme.igPrimitives.gray[900]}
      strokeWidth={1}
    />
  );
};

interface AudienceByMetricChartProps {
  config: GraphConfig;
  filterMethod?: string;
  groupMetricData: LineMetricData[];
  legends: LineData[];
  syncId?: string;
  dateFormat?: string;
  graphHeight?: number;
}

export const AudienceByMetricChart = ({
  config,
  filterMethod,
  groupMetricData,
  legends,
  syncId,
  dateFormat,
  graphHeight,
}: AudienceByMetricChartProps) => {
  const theme = useTheme();
  const graphValueFormatter = useGraphValueFormatter();
  const sitewidePageName = useSelector(getSitewidePageName);
  const expPageName = useSelector(getExperienceAnalyticsPageName);
  const currentExperience = useSelector(selectedExperience);
  const dataType = useMemo(
    () => [filterMethod ? METRIC_TO_DATA_TYPE[filterMethod] : "number"],
    [filterMethod]
  );

  const graphColors = useMemo(() => {
    const colors = generateDistinctGraphColors(legends.length);
    const otherLegendIndex = legends.findIndex(
      (legend) => legend.name === "Other"
    );
    if (otherLegendIndex) {
      colors[otherLegendIndex] = theme.igPrimitives.gray[900];
    }
    return colors;
  }, [legends]);

  const [baseFocusGroups, setBaseFocusGroups] = useImmer<string[]>([]);

  const tooltipProps = useGetTooltipProps({
    colors: graphColors,
    dataTypes: dataType,
    legends: legends.filter((line) => !baseFocusGroups.includes(line.id)),
    dateFormat,
  });

  const handleLegendClick = (dataKey: string) => {
    if (baseFocusGroups.includes(dataKey)) {
      setBaseFocusGroups(baseFocusGroups.filter((el) => el !== dataKey));
    } else {
      setBaseFocusGroups((prev) => [...prev, dataKey]);
    }
    mpAnalyticsViewChanged({
      pageType: currentExperience?.id
        ? getAnalyticsPageType(currentExperience.category ?? "experiment")
        : "sitewide",
      pageName: currentExperience?.id ? expPageName : sitewidePageName,
      changeType: "chart_filter",
      filterChange: dataKey,
      chartName: config.title?.[0],
    });
  };

  return (
    <ResponsiveContainer width="100%" height={graphHeight ?? 400}>
      <BarChart
        data={groupMetricData}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        syncId={syncId}
      >
        <CartesianGrid
          style={{ stroke: theme.igBorders.core.disabled }}
          vertical={false}
        />

        <XAxis
          axisLine={{ stroke: theme.igBorders.core.disabled }}
          dataKey={"xAxisKey"}
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
              }}
              position={"insideBottom"}
              offset={-5}
              textAnchor={"middle"}
            />
          }
          tick={(props) => (
            <AxisTick
              config={config}
              graphValueFormatter={graphValueFormatter}
              groupMetricData={groupMetricData}
              dateFormat={dateFormat}
              {...props}
            />
          )}
          tickLine={false}
        />

        <YAxis
          axisLine={false}
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
                marginTop: "10px",
                textAnchor: "middle",
              }}
              angle={270}
              position={"insideLeft"}
              offset={10}
              textAnchor={"middle"}
            />
          }
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          tickFormatter={(value: any) =>
            graphValueFormatter({
              isAxisTick: true,
              value,
              configDataTypes: [
                filterMethod ? METRIC_TO_DATA_TYPE[filterMethod] : "number",
              ],
            })!
          }
          tickLine={false}
        />

        <Tooltip {...tooltipProps} cursor={<CustomCursor />} />

        {config.showLegend && legends.length > 1 && (
          <Legend
            content={
              <InteractiveChartLegend
                onLegendClick={handleLegendClick}
                focusGroups={baseFocusGroups}
                lines={legends}
                colors={graphColors}
              />
            }
            wrapperStyle={{ paddingTop: 24 }}
          />
        )}

        {legends.map((legend, index) => (
          <Bar
            dataKey={legend.id}
            fill={graphColors[index]}
            hide={baseFocusGroups.includes(legend.id)}
            key={index}
            stackId="a"
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};
